import React, { useState, useEffect, useMemo } from 'react';
import { Settings, GripVertical, Filter } from 'lucide-react';
import { TakeoffService } from '../services/TakeoffService';
import { supabase } from '../lib/supabase';
import { Table, Button, Badge, SearchBar, Popover, Checkbox, Dropdown } from './ui';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import NewTakeoffModal from './NewTakeoffModal';
import TakeoffDetailsModal from './TakeoffDetailsModal';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Section Header Component
const SectionHeader = ({ title, count, isExpanded, onToggle }) => (
  <div
    onClick={onToggle}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-2)',
      padding: 'var(--space-3) var(--space-4)',
      backgroundColor: 'var(--dark-surface)',
      borderBottom: '1px solid var(--gray-700)',
      cursor: 'pointer',
      userSelect: 'none',
      fontWeight: 'var(--font-semibold)',
      fontSize: 'var(--text-sm)',
      color: 'var(--gray-200)',
      transition: 'background-color 0.2s ease'
    }}
    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--dark-elevated)'}
    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--dark-surface)'}
  >
    <span style={{ fontSize: '12px', color: 'var(--gray-400)' }}>
      {isExpanded ? '▼' : '►'}
    </span>
    <span>{title}</span>
    <span style={{
      padding: '2px 8px',
      backgroundColor: 'var(--gray-700)',
      borderRadius: 'var(--radius-full)',
      fontSize: '11px',
      fontWeight: 'var(--font-medium)',
      color: 'var(--gray-300)'
    }}>
      {count}
    </span>
  </div>
);

// Filter Chip Component
const FilterChip = ({ label, onRemove }) => (
  <div style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--space-1)',
    padding: '4px 10px',
    backgroundColor: 'var(--dark-elevated)',
    borderRadius: 'var(--radius-full)',
    fontSize: 'var(--text-sm)',
    color: 'var(--gray-200)',
    border: '1px solid var(--gray-600)'
  }}>
    <span>{label}</span>
    <button
      onClick={onRemove}
      style={{
        background: 'none',
        border: 'none',
        padding: '0',
        color: 'var(--gray-400)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        fontSize: '16px',
        lineHeight: '1'
      }}
      onMouseOver={(e) => e.currentTarget.style.color = 'var(--gray-200)'}
      onMouseOut={(e) => e.currentTarget.style.color = 'var(--gray-400)'}
    >
      ×
    </button>
  </div>
);

// Sortable Column Item Component
const SortableColumnItem = ({ column, checked, onChange }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-2)',
        backgroundColor: isDragging ? 'var(--dark-elevated)' : 'transparent',
        borderRadius: 'var(--radius-md)',
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
    >
      <div
        {...attributes}
        {...listeners}
        style={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          color: 'var(--gray-400)',
          padding: '4px',
        }}
      >
        <GripVertical size={16} />
      </div>
      <Checkbox
        label={column.label}
        checked={checked}
        onChange={onChange}
      />
    </div>
  );
};

/**
 * TakeoffsTable
 *
 * Standardized takeoffs table using the new UI component library.
 * Reference implementation: ClientsTable (src/components/ClientsTable.js)
 *
 * Features:
 * - Server-side pagination (20/50/All)
 * - Server-side search with debouncing
 * - Column sorting (all columns)
 * - Column resizing with auto-size
 * - Column visibility management
 * - Drag-and-drop column reordering
 * - Real-time updates
 * - Status tags with colors
 * - Price formatting
 * - Row click to view details
 * - New takeoff modal
 */
const TakeoffsTable = () => {
  const [takeoffs, setTakeoffs] = useState([]);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('jobNumber');
  const [sortDirection, setSortDirection] = useState('desc'); // Default highest job number first
  const [showNewTakeoffModal, setShowNewTakeoffModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTakeoff, setSelectedTakeoff] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20); // Default to 20
  const [autoResizeEnabled, setAutoResizeEnabled] = useState(true); // Auto-resize enabled by default
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('takeoffs-visible-columns');
    return saved ? JSON.parse(saved) : {
      jobNumber: true,
      projectName: true,
      status: true,
      clientName: true,
      contactName: true,
      ufredoPrice: true,
      abCivilPrice: true,
      completionNeededDate: true,
      cadFileAvailable: true,
      email: true,
    };
  });
  const [columnOrder, setColumnOrder] = useState(() => {
    const saved = localStorage.getItem('takeoffs-column-order');
    return saved ? JSON.parse(saved) : [];
  });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    cadFileAvailable: '',
    invoicedStatus: ''
  });
  const [expandedSections, setExpandedSections] = useState({
    working: true,  // "Working on it" section expanded by default
    completed: false // "Completed" section collapsed by default
  });

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = columnOrder.indexOf(active.id);
      const newIndex = columnOrder.indexOf(over.id);

      const newOrder = arrayMove(columnOrder, oldIndex, newIndex);
      setColumnOrder(newOrder);
      localStorage.setItem('takeoffs-column-order', JSON.stringify(newOrder));
    }
  };

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Initial load
  useEffect(() => {
    window.scrollTo(0, 0);
    loadTakeoffs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load takeoffs from server
  const loadTakeoffs = async (page = currentPage, search = debouncedSearchQuery, size = pageSize) => {
    try {
      setLoading(true);
      setError(null);

      // Load takeoffs, clients, and contacts in parallel
      const [result, clientsData, contactsData] = await Promise.all([
        TakeoffService.getPaginatedTakeoffs(
          page,
          size,
          sortField,
          sortDirection,
          search,
          filters
        ),
        supabase.from('clients').select('*').order('name'),
        supabase.from('contacts').select('*').order('name')
      ]);

      setTakeoffs(result.data);
      setTotalPages(result.totalPages);
      setTotalCount(result.totalCount);
      setCurrentPage(result.currentPage);

      // Set clients and contacts
      if (clientsData.data) setClients(clientsData.data);
      if (contactsData.data) setContacts(contactsData.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Update when sort, search, or filters change
  useEffect(() => {
    if (!loading) {
      setCurrentPage(1);
      loadTakeoffs(1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDirection, debouncedSearchQuery, filters.status, filters.cadFileAvailable, filters.invoicedStatus]);

  // Format currency
  const formatCurrency = (value) => {
    if (!value) return '—';
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/[$,]/g, '')) : value;
    return isNaN(numValue) ? value : `$${numValue.toLocaleString()}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  // Get status badge variant
  const getStatusBadgeVariant = (status) => {
    if (!status) return 'neutral';
    const statusLower = status.toLowerCase();
    if (statusLower.includes('completed') || statusLower.includes('done')) return 'success';
    if (statusLower.includes('working') || statusLower.includes('progress')) return 'info';
    if (statusLower.includes('pending') || statusLower.includes('waiting')) return 'warning';
    if (statusLower.includes('cancelled') || statusLower.includes('rejected')) return 'danger';
    return 'neutral';
  };

  // Handle row click
  const handleRowClick = (takeoff) => {
    setSelectedTakeoff(takeoff);
    setShowDetailsModal(true);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      loadTakeoffs(newPage);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    loadTakeoffs(1, debouncedSearchQuery, newSize);
  };

  // Handle manual column resize - auto-disable auto-resize
  const handleManualResize = () => {
    if (autoResizeEnabled) {
      setAutoResizeEnabled(false);
    }
  };

  // Handle sort events from Table component
  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
  };

  // Handle takeoff created
  const handleTakeoffCreated = (newTakeoff) => {
    setShowNewTakeoffModal(false);
    loadTakeoffs(); // Reload to get fresh data
  };

  // Handle takeoff updated
  const handleTakeoffUpdated = (updatedTakeoff) => {
    setShowDetailsModal(false);
    setSelectedTakeoff(null);
    loadTakeoffs(); // Reload to get fresh data
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      status: '',
      cadFileAvailable: '',
      invoicedStatus: ''
    });
  };

  // All available columns (before filtering for visibility)
  const allColumns = useMemo(() => [
    {
      id: 'jobNumber',
      label: 'Job Number',
      field: 'jobNumber',
      width: 120,
      minWidth: 100,
      maxWidth: 180,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'projectName',
      label: 'Project Name',
      field: 'projectName',
      width: 250,
      minWidth: 150,
      maxWidth: 400,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'status',
      label: 'Status',
      field: 'status',
      width: 140,
      minWidth: 100,
      maxWidth: 200,
      sortable: true,
      resizable: true,
      contentType: 'badge',
      render: (value) =>
        value ? (
          <Badge variant={getStatusBadgeVariant(value)} size="sm">
            {value}
          </Badge>
        ) : (
          '—'
        ),
    },
    {
      id: 'clientName',
      label: 'Client',
      field: 'clientName',
      width: 180,
      minWidth: 120,
      maxWidth: 300,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'contactName',
      label: 'Contact',
      field: 'contactName',
      width: 180,
      minWidth: 120,
      maxWidth: 300,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'ufredoPrice',
      label: 'Ufredo Price',
      field: 'ufredoPrice',
      width: 130,
      minWidth: 100,
      maxWidth: 180,
      sortable: true,
      resizable: true,
      align: 'right',
      contentType: 'text',
      formatValue: (value) => formatCurrency(value),
      render: (value) => formatCurrency(value),
    },
    {
      id: 'abCivilPrice',
      label: 'AB Civil Price',
      field: 'abCivilPrice',
      width: 140,
      minWidth: 100,
      maxWidth: 180,
      sortable: true,
      resizable: true,
      align: 'right',
      contentType: 'text',
      formatValue: (value) => formatCurrency(value),
      render: (value) => formatCurrency(value),
    },
    {
      id: 'completionNeededDate',
      label: 'Due Date',
      field: 'completionNeededDate',
      width: 120,
      minWidth: 100,
      maxWidth: 180,
      sortable: true,
      resizable: true,
      contentType: 'text',
      formatValue: (value) => formatDate(value),
      render: (value) => formatDate(value),
    },
    {
      id: 'cadFileAvailable',
      label: 'CAD Available',
      field: 'cadFileAvailable',
      width: 130,
      minWidth: 100,
      maxWidth: 180,
      sortable: true,
      resizable: true,
      align: 'center',
      contentType: 'badge',
      render: (value) => {
        if (!value) return '—';
        const isYes = value.toLowerCase() === 'yes';
        return (
          <Badge variant={isYes ? 'success' : 'neutral'} size="sm">
            {value}
          </Badge>
        );
      },
    },
    {
      id: 'email',
      label: 'Email',
      field: 'email',
      width: 220,
      minWidth: 150,
      maxWidth: 350,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'link',
      render: (value) =>
        value ? (
          <a
            href={`mailto:${value}`}
            style={{ color: 'var(--cta-blue)', textDecoration: 'none' }}
            onClick={(e) => e.stopPropagation()}
          >
            {value}
          </a>
        ) : (
          '—'
        ),
    },
  ], []);

  // Initialize column order if empty
  useEffect(() => {
    if (columnOrder.length === 0 && allColumns.length > 0) {
      const defaultOrder = allColumns.map(col => col.id);
      setColumnOrder(defaultOrder);
      localStorage.setItem('takeoffs-column-order', JSON.stringify(defaultOrder));
    }
  }, [allColumns, columnOrder.length]);

  // Get visible columns with custom ordering
  const columns = useMemo(() => {
    if (columnOrder.length === 0) {
      return allColumns.filter(col => visibleColumns[col.id]);
    }

    const orderedColumns = columnOrder
      .map(colId => allColumns.find(col => col.id === colId))
      .filter(col => col && visibleColumns[col.id]);

    return orderedColumns;
  }, [allColumns, columnOrder, visibleColumns]);

  // Group takeoffs by status
  const groupedTakeoffs = useMemo(() => {
    const working = takeoffs.filter(t =>
      !t.status || t.status.toLowerCase() === 'new' || t.status.toLowerCase().includes('working') ||
      t.status.toLowerCase().includes('progress') || t.status.toLowerCase().includes('pending') ||
      t.status.toLowerCase().includes('waiting')
    );
    const completed = takeoffs.filter(t =>
      t.status && t.status.toLowerCase().includes('complete')
    );
    return { working, completed };
  }, [takeoffs]);

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading && takeoffs.length === 0) {
    return <LoadingSpinner message="Loading takeoffs..." />;
  }

  return (
    <div style={{ padding: '24px', backgroundColor: 'var(--dark-bg)', minHeight: '100vh' }}>
      {/* Breadcrumb Navigation */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-6)',
          fontSize: 'var(--text-sm)',
          color: 'var(--gray-400)',
        }}
      >
        <span style={{ fontWeight: 'var(--font-medium)' }}>3D Models</span>
        <span>{'>'}</span>
        <span style={{ color: 'var(--gray-200)', fontWeight: 'var(--font-semibold)' }}>Takeoffs</span>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <ErrorMessage
            message={error}
            onRetry={loadTakeoffs}
            onDismiss={() => setError(null)}
          />
        </div>
      )}

      {/* Page Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-6)',
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 'var(--text-3xl)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--gray-50)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
            }}
          >
            Takeoffs
          </h1>
          <p
            style={{
              margin: 0,
              marginTop: 'var(--space-2)',
              fontSize: 'var(--text-sm)',
              color: 'var(--gray-400)',
            }}
          >
            {totalCount} total takeoffs
          </p>
        </div>

        <Button
          variant="primary"
          size="md"
          onClick={() => setShowNewTakeoffModal(true)}
        >
          + New Takeoff
        </Button>
      </div>

      {/* Table with integrated toolbar */}
      <div style={{
        border: '1px solid var(--gray-600)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden'
      }}>
        {/* Search Bar and Auto-resize Toolbar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--space-4)',
            backgroundColor: 'var(--dark-bg)',
            borderBottom: '1px solid var(--gray-600)',
            gap: 'var(--space-4)',
          }}
        >
          <div style={{ width: '300px', flexShrink: 0 }}>
            <SearchBar
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search takeoffs..."
              size="sm"
            />
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Popover
              isOpen={showFilters}
              onClose={() => setShowFilters(false)}
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter size={16} />
                  Filters
                  {(filters.status || filters.cadFileAvailable || filters.invoicedStatus) && (
                    <span style={{
                      marginLeft: '4px',
                      backgroundColor: 'var(--cta-orange)',
                      color: 'white',
                      borderRadius: 'var(--radius-full)',
                      padding: '2px 6px',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-semibold)'
                    }}>
                      {[filters.status, filters.cadFileAvailable, filters.invoicedStatus].filter(Boolean).length}
                    </span>
                  )}
                </Button>
              }
              position="bottom-right"
              width="600px"
              maxWidth="90vw"
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                <h3 style={{
                  margin: 0,
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)'
                }}>
                  Filter Takeoffs
                </h3>

                {/* Status Filter */}
                <Dropdown
                  label="Status"
                  value={filters.status}
                  options={[
                    { value: '', label: 'All statuses' },
                    { value: 'Completed', label: 'Completed' },
                    { value: 'Working', label: 'Working' },
                    { value: 'In Progress', label: 'In Progress' },
                    { value: 'Pending', label: 'Pending' },
                    { value: 'Waiting', label: 'Waiting' }
                  ]}
                  onChange={(value) => setFilters({ ...filters, status: value })}
                />

                {/* CAD File Available Filter */}
                <Dropdown
                  label="CAD File Available"
                  value={filters.cadFileAvailable}
                  options={[
                    { value: '', label: 'All' },
                    { value: 'Yes', label: 'Yes' },
                    { value: 'No', label: 'No' }
                  ]}
                  onChange={(value) => setFilters({ ...filters, cadFileAvailable: value })}
                />

                {/* Invoiced Status Filter */}
                <Dropdown
                  label="Invoiced Status"
                  value={filters.invoicedStatus}
                  options={[
                    { value: '', label: 'All' },
                    { value: 'Invoiced', label: 'Invoiced' },
                    { value: 'Not Invoiced', label: 'Not Invoiced' },
                    { value: 'Paid', label: 'Paid' }
                  ]}
                  onChange={(value) => setFilters({ ...filters, invoicedStatus: value })}
                />

                {/* Footer */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  borderTop: '1px solid var(--gray-700)',
                  paddingTop: 'var(--space-3)'
                }}>
                  <Button variant="ghost" size="sm" onClick={clearAllFilters}>
                    Clear all
                  </Button>
                  <Button variant="primary" size="sm" onClick={() => setShowFilters(false)}>
                    Apply
                  </Button>
                </div>
              </div>
            </Popover>

            <Popover
              isOpen={showColumnSettings}
              onClose={() => setShowColumnSettings(false)}
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowColumnSettings(!showColumnSettings)}
                >
                  <Settings size={16} />
                  Columns
                </Button>
              }
              position="bottom-right"
              width="520px"
              maxWidth="90vw"
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {/* Header with count */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: 'var(--text-base)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--gray-200)'
                  }}>
                    Toggle Columns
                  </h3>
                  <span style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--gray-400)',
                    backgroundColor: 'var(--dark-elevated)',
                    padding: '4px 10px',
                    borderRadius: 'var(--radius-full)',
                    fontWeight: 'var(--font-medium)'
                  }}>
                    {Object.values(visibleColumns).filter(Boolean).length} / {Object.keys(visibleColumns).length} visible
                  </span>
                </div>

                {/* Quick Actions */}
                <div style={{
                  display: 'flex',
                  gap: 'var(--space-2)',
                  paddingBottom: 'var(--space-3)',
                  borderBottom: '1px solid var(--gray-700)',
                  flexWrap: 'wrap'
                }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const allVisible = {};
                      Object.keys(visibleColumns).forEach(key => {
                        allVisible[key] = true;
                      });
                      setVisibleColumns(allVisible);
                      localStorage.setItem('takeoffs-visible-columns', JSON.stringify(allVisible));
                    }}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const allHidden = {};
                      Object.keys(visibleColumns).forEach(key => {
                        allHidden[key] = false;
                      });
                      setVisibleColumns(allHidden);
                      localStorage.setItem('takeoffs-visible-columns', JSON.stringify(allHidden));
                    }}
                  >
                    Deselect All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const defaultOrder = allColumns.map(col => col.id);
                      setColumnOrder(defaultOrder);
                      localStorage.setItem('takeoffs-column-order', JSON.stringify(defaultOrder));
                    }}
                  >
                    Reset Order
                  </Button>
                </div>

                {/* Column Checkboxes with Drag & Drop */}
                <div style={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  paddingRight: 'var(--space-2)'
                }}>
                  {/* Visible Columns (Checked) */}
                  <div style={{
                    marginBottom: 'var(--space-3)'
                  }}>
                    <div style={{
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-semibold)',
                      color: 'var(--gray-400)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: 'var(--space-2)',
                      paddingLeft: 'var(--space-2)'
                    }}>
                      Visible Columns
                    </div>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={columnOrder.filter(id => visibleColumns[id])}
                        strategy={verticalListSortingStrategy}
                      >
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 'var(--space-1)'
                        }}>
                          {columnOrder
                            .filter(id => visibleColumns[id])
                            .map(colId => {
                              const col = allColumns.find(c => c.id === colId);
                              if (!col) return null;
                              return (
                                <SortableColumnItem
                                  key={col.id}
                                  column={col}
                                  checked={visibleColumns[col.id]}
                                  onChange={(e) => {
                                    const newVisibleColumns = {
                                      ...visibleColumns,
                                      [col.id]: e.target.checked
                                    };
                                    setVisibleColumns(newVisibleColumns);
                                    localStorage.setItem('takeoffs-visible-columns', JSON.stringify(newVisibleColumns));
                                  }}
                                />
                              );
                            })}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </div>

                  {/* Hidden Columns (Unchecked) */}
                  {columnOrder.filter(id => !visibleColumns[id]).length > 0 && (
                    <div style={{
                      paddingTop: 'var(--space-3)',
                      borderTop: '1px solid var(--gray-700)'
                    }}>
                      <div style={{
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-semibold)',
                        color: 'var(--gray-400)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: 'var(--space-2)',
                        paddingLeft: 'var(--space-2)'
                      }}>
                        Hidden Columns
                      </div>
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={columnOrder.filter(id => !visibleColumns[id])}
                          strategy={verticalListSortingStrategy}
                        >
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'var(--space-1)'
                          }}>
                            {columnOrder
                              .filter(id => !visibleColumns[id])
                              .map(colId => {
                                const col = allColumns.find(c => c.id === colId);
                                if (!col) return null;
                                return (
                                  <SortableColumnItem
                                    key={col.id}
                                    column={col}
                                    checked={visibleColumns[col.id]}
                                    onChange={(e) => {
                                      const newVisibleColumns = {
                                        ...visibleColumns,
                                        [col.id]: e.target.checked
                                      };
                                      setVisibleColumns(newVisibleColumns);
                                      localStorage.setItem('takeoffs-visible-columns', JSON.stringify(newVisibleColumns));
                                    }}
                                  />
                                );
                              })}
                          </div>
                        </SortableContext>
                      </DndContext>
                    </div>
                  )}
                </div>
              </div>
            </Popover>

            <Button
              variant={autoResizeEnabled ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setAutoResizeEnabled(!autoResizeEnabled)}
            >
              {autoResizeEnabled ? '✓ ' : ''}Auto-resize
            </Button>
          </div>
        </div>

        {/* Filter Chips Display */}
        {(filters.status || filters.cadFileAvailable || filters.invoicedStatus) && (
          <div style={{
            display: 'flex',
            gap: 'var(--space-2)',
            flexWrap: 'wrap',
            padding: 'var(--space-4)',
            paddingTop: 'var(--space-3)',
            paddingBottom: 'var(--space-3)',
            backgroundColor: 'var(--dark-bg)',
            borderBottom: '1px solid var(--gray-600)',
            alignItems: 'center'
          }}>
            <span style={{ color: 'var(--gray-400)', fontSize: 'var(--text-sm)' }}>
              Active filters:
            </span>

            {filters.status && (
              <FilterChip
                label={`Status: ${filters.status}`}
                onRemove={() => setFilters({ ...filters, status: '' })}
              />
            )}

            {filters.cadFileAvailable && (
              <FilterChip
                label={`CAD Available: ${filters.cadFileAvailable}`}
                onRemove={() => setFilters({ ...filters, cadFileAvailable: '' })}
              />
            )}

            {filters.invoicedStatus && (
              <FilterChip
                label={`Invoiced: ${filters.invoicedStatus}`}
                onRemove={() => setFilters({ ...filters, invoicedStatus: '' })}
              />
            )}

            <Button variant="ghost" size="sm" onClick={clearAllFilters}>
              Clear all
            </Button>
          </div>
        )}

        {/* Working on it Section */}
        <div style={{ marginBottom: 'var(--space-2)' }}>
          <SectionHeader
            title="Working on it"
            count={groupedTakeoffs.working.length}
            isExpanded={expandedSections.working}
            onToggle={() => toggleSection('working')}
          />
          {expandedSections.working && groupedTakeoffs.working.length > 0 && (
            <Table
              data={groupedTakeoffs.working}
              columns={columns}
              sortable={true}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              searchable={false}
              resizable={true}
              paginated={false}
              autoResizeOnDataChange={autoResizeEnabled}
              showAutoSizeButton={false}
              storageKey="takeoffs-table"
              loading={loading}
              emptyMessage="No takeoffs in this section"
              onRowClick={handleRowClick}
              onManualResize={handleManualResize}
              variant="dense"
              stickyHeader={false}
              style={{ border: 'none', borderRadius: '0', overflow: 'visible', marginBottom: '0' }}
            />
          )}
        </div>

        {/* Completed Section */}
        <div style={{ marginBottom: 'var(--space-2)' }}>
          <SectionHeader
            title="Completed"
            count={groupedTakeoffs.completed.length}
            isExpanded={expandedSections.completed}
            onToggle={() => toggleSection('completed')}
          />
          {expandedSections.completed && groupedTakeoffs.completed.length > 0 && (
            <Table
              data={groupedTakeoffs.completed}
              columns={columns}
              sortable={true}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              searchable={false}
              resizable={true}
              paginated={false}
              autoResizeOnDataChange={autoResizeEnabled}
              showAutoSizeButton={false}
              storageKey="takeoffs-table-completed"
              loading={loading}
              emptyMessage="No completed takeoffs"
              onRowClick={handleRowClick}
              onManualResize={handleManualResize}
              variant="dense"
              stickyHeader={false}
              style={{ border: 'none', borderRadius: '0', overflow: 'visible' }}
            />
          )}
        </div>

      {/* Custom Server-side Pagination */}
      {totalCount > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--space-4)',
            backgroundColor: 'var(--dark-bg)',
            borderTop: '1px solid var(--gray-600)',
            gap: 'var(--space-4)',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
              Page {currentPage} of {totalPages} ({totalCount} total takeoffs)
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                Show:
              </span>
              <Button
                variant={pageSize === 20 ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => handlePageSizeChange(20)}
              >
                20
              </Button>
              <Button
                variant={pageSize === 50 ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => handlePageSizeChange(50)}
              >
                50
              </Button>
              <Button
                variant={pageSize === 999999 ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => handlePageSizeChange(999999)}
              >
                All
              </Button>
            </div>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              >
                First
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </Button>

              {/* Page numbers */}
              {(() => {
                const pages = [];
                const startPage = Math.max(1, currentPage - 2);
                const endPage = Math.min(totalPages, currentPage + 2);

                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <Button
                      key={i}
                      variant={i === currentPage ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => handlePageChange(i)}
                    >
                      {i}
                    </Button>
                  );
                }

                return pages;
              })()}

              <Button
                variant="ghost"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last
              </Button>
            </div>
          )}
        </div>
      )}
      </div>

      {/* New Takeoff Modal */}
      {showNewTakeoffModal && (
        <NewTakeoffModal
          isOpen={showNewTakeoffModal}
          onClose={() => setShowNewTakeoffModal(false)}
          onTakeoffCreated={handleTakeoffCreated}
        />
      )}

      {/* Takeoff Details Modal */}
      {showDetailsModal && (
        <TakeoffDetailsModal
          takeoff={selectedTakeoff}
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedTakeoff(null);
          }}
          clients={clients}
          contacts={contacts}
          onUpdate={handleTakeoffUpdated}
        />
      )}
    </div>
  );
};

export default TakeoffsTable;
